import { TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { DateDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/DateDataDefinition";
import { DeleteGrayIcon } from "../Icon/DeleteGrayIcon";

import { IEditModeValueEditor } from "./Common";
import { DeleteButton, Row } from "./DesignUI";
import { StyledLink } from "../BuildlingBlocks/StyledLink";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import type { AppGlueAny } from "../../rule-engine-core/Common/AppGlueAny";

export { DisplayModeValueEditor as DisplayModeDateValueEditor } from "./DisplayModeRender";

export interface EditorDateListEditorProps {
    values: AppGlueAny[];
    autFocus?: boolean;
    updateCallback: (vals: string[]) => void;
    type?: string;
}

export const EditorDateListEditor: React.FC<EditorDateListEditorProps> = (props: EditorDateListEditorProps) => {
    const [values, setValues] = useState<AppGlueAny[]>(props.values);

    useEffect(() => {
        setValues([...props.values]);
    }, [props.values, setValues]);

    const update = (vals: string[]) => {
        props.updateCallback(vals);

        setValues([...vals]);
    };

    const onChange = (event: React.ChangeEvent<HTMLInputElement>, selectedLine: number) => {
        if (selectedLine >= 0) {
            values[selectedLine] = event.target.value;
            update(values);
        }
    };

    const add = () => {
        values.push("");
        update(values);
    };

    const remove = (index: number) => {
        values.splice(index, 1);
        update(values);
    };

    return (
        <StyledDateList className="PropertyList-Wrapper DateList-Wrapper">
            <div className="DateList">
                {values &&
                    values.map((val: string, idx: number) => (
                        <Row key={idx}>
                            <TextField
                                value={val}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e, idx)}
                                type={props.type || "date"}
                                autoFocus={props.autFocus && idx === 0}
                            />
                            <DeleteButton
                                onClick={() => remove(idx)}
                                className="ValueEditor-delete-line"
                            >
                                <DeleteGrayIcon />
                            </DeleteButton>
                        </Row>
                    ))}
            </div>
            <StyledLink
                onClick={add}
                sx={{ background: UIStyleConstants.BLUE_COLOR }}
                classes={{ root: "PropertyList-Add" }}
            >
                Add
            </StyledLink>
        </StyledDateList>
    );
};

export const EditModeDateValueEditor = React.forwardRef<HTMLElement, IEditModeValueEditor>(
    ({ value, forData, onUpdate }: IEditModeValueEditor, ref) => {
        let values = forData.list
            ? Array.isArray(value) && typeof value !== "string"
                ? value
                : value
                  ? [value]
                  : []
            : Array.isArray(value) && typeof value !== "string"
              ? value[0]
              : value;

        const onValueUpdate = (newValues: string[]) => {
            onUpdate(newValues);
        };

        if (forData.list) {
            return (
                <EditorDateListEditor
                    values={values}
                    updateCallback={onValueUpdate}
                />
            );
        }

        return (
            <TextField
                variant={"outlined"}
                size={"small"}
                value={value as string}
                type={(forData as DateDataDefinition).storeTime ? "datetime-local" : "date"}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    onUpdate(event.target.value);
                }}
                placeholder={forData.name}
                data-testid={`ValueEditor-Edit-date`}
                inputProps={{
                    min: (forData as DateDataDefinition).lowerBounds,
                    max: (forData as DateDataDefinition).upperBounds,
                }}
                InputLabelProps={{ shrink: true, required: true }}
                inputRef={ref}
            />
        );
    },
);

const StyledDateList = styled.div`
    width: 100%;
    text-align: right;

    .DateList {
        display: flex;
        flex-direction: column;
        margin-bottom: 10px;

        > * {
            margin-top: 10px;
        }

        input[type="date"] {
            position: relative;

            &::-webkit-calendar-picker-indicator {
                position: absolute;
                right: 20px;
                top: 50%;
                transform: translateY(-50%);
                height: 16px;
                width: 16px;
                opacity: 0;
                color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
            }

            &:hover::-webkit-calendar-picker-indicator {
                opacity: 1;
            }
        }
    }

    .DateList-Label {
        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

        font-style: normal;
        font-weight: 600;
        margin-bottom: 5px;
    }

    .DateList-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
`;
